import { defineComponent as _defineComponent } from 'vue'
import type { HoleType, PartType, SlotType } from '@/partTypes'
import Scene from '@/util/dealPlankExtraDraw'
import {
  dealCurveHoles,
  getPathsMaxAreaObj,
  getThroughPathObj,
} from '@/util/drawPlankFuncs'
import { getSlotBasicInfo } from '@/util/plankCommonFuncs'
import { nextTick, reactive, ref, watch } from 'vue'

import { defaultTipsArr } from './data'

// 绘制的起点

export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  props: {
    isShow: { type: Boolean, default: false },
    depthErrorData: null,
    depthErrorIndex: { default: 0 },
    depthErrorNum: { default: 0 },
    allDepthErrorData: null
  },
  emits: ['onClose', 'onDepthErrorBtn', 'onSearch'],
  setup(__props: any, { emit: emits }) {

const props = __props as { isShow: boolean, depthErrorData: any, depthErrorIndex: number, depthErrorNum: number, allDepthErrorData: any }

const _startX = 50,
  _startY = 100
// 绘制冲突框时设置边框和图形之间的间距
const gap = 2


const scene = ref<InstanceType<typeof Scene>>()
const canvasRef = ref<HTMLCanvasElement>()
const ctx = ref<CanvasRenderingContext2D | null>()
const zoomPercent = ref(100)
const scale = ref(1) // 缩放倍数
const clipPath = ref<{ x: number; y: number }[]>([])

const startXBase = ref(0)
const startYBase = ref(0)
const canvasState = reactive({
  width: 400,
  height: 400,
})
// 是否点击了框出冲突部分
const isClickFrameSelect = ref(false)
// 渲染
function startRender() {
  ctx.value?.clearRect(0, 0, 1000, 1000)
  ctx.value!.strokeStyle = '#000'

  const { rect } = props.depthErrorData!
  const [baseWidth, baseHeight] = [
    canvasState.width - 20,
    canvasState.height - 20,
  ]
  startXBase.value = _startX
  startYBase.value = _startY

  // 宽高比
  scale.value = +Math.min(
    baseWidth / rect.width,
    baseHeight / rect.height
  ).toFixed(1)
  // 板件绘制宽高
  const drawWidth = calcScale(rect.width)
  const drawHeight = calcScale(rect.height)
  // 居中绘制
  if (drawWidth < baseWidth) {
    const w = (baseWidth - drawWidth) / 2
    startXBase.value += w
  }
  if (drawHeight < baseHeight) {
    const h = (baseHeight - drawHeight) / 2
    startYBase.value += h
  }
  drawShape(startXBase.value, startYBase.value, props.depthErrorData!)
  // 创建裁剪路径
  ctx.value?.save()
  clipPath.value.forEach((it, idx) => {
    if (idx) {
      ctx.value?.lineTo(it.x, it.y)
    } else {
      ctx.value?.moveTo(it.x, it.y)
    }
  })
  // 裁剪
  ctx.value?.clip()
  drawHoles(startXBase.value, startYBase.value, props.depthErrorData!)
  drawSlots(startXBase.value, startYBase.value, props.depthErrorData!)
  // 恢复路径
  ctx.value?.restore()
  clipPath.value = []
}
// 绘制孔
function drawHoles(startX: number, startY: number, part: PartType) {
  const { holes } = part
  for (const hole of holes) {
    ctx.value?.beginPath()
    if (hole.side == 1) {
      ctx.value!.strokeStyle = '#f008'
    } else {
      ctx.value!.strokeStyle = '#00f8'
    }

    ctx.value?.arc(
      startX + calcScale(hole.center.x),
      startY + calcScale(hole.center.y),
      calcScale(hole.diameter / 2),
      0,
      Math.PI * 2
    )
    if (hole.depthError) {
      ctx.value!.fillStyle = '#ffff00'
      ctx.value?.fill()
    }
    ctx.value?.stroke()
    ctx.value?.closePath()
  }
}
// 绘制槽
function drawSlots(startX: number, startY: number, part: PartType) {
  const { slots } = part
  for (const slot of slots) {
    const { x, y, width, height } = getSlotBasicInfo(slot, 'pt')
    ctx.value?.beginPath()
    if (slot.side == 1) {
      ctx.value!.strokeStyle = '#f008'
    } else {
      ctx.value!.strokeStyle = '#00f8'
    }
    if (slot.depthError) {
      ctx.value!.fillStyle = '#ffff00'
      ctx.value?.fillRect(
        startX + calcScale(x),
        startY + calcScale(y),
        calcScale(width),
        calcScale(height)
      )
    }
    ctx.value?.strokeRect(
      startX + calcScale(x),
      startY + calcScale(y),
      calcScale(width),
      calcScale(height)
    )
    ctx.value?.closePath()
  }
}
// 绘制小板轮廓
function drawShape(startX: number, startY: number, part: PartType) {
  ctx.value!.fillStyle = 'transparent'
  const [width, height] = [
    calcScale(part.rect.width),
    calcScale(part.rect.height),
  ]
  if (!part.path) {
    ctx.value?.fillRect(startX, startY, width, height)
    ctx.value?.strokeRect(startX, startY, width, height)
    clipPath.value = [
      { x: startX, y: startY },
      { x: startX + width, y: startY },
      { x: startX + width, y: startY + height },
      { x: startX, y: startY + height },
    ]
  } else {
    // 记录板件轮廓的点位顺逆时针
    let area = window.ClipperLib.JS.AreaOfPolygon(part.path[0])
    let firstDirection = area > 0 ? false : true
    ctx.value?.beginPath()
    // 打穿长圆孔的加入异形的绘制
    const finalCurveHoles = dealCurveHoles(part.curveHoles)
    const throughCurveHoles = getThroughPathObj(finalCurveHoles, part.thick)
    // 获取面积最大的牛角槽
    const maxMillInfo = getPathsMaxAreaObj(part.millInfo, 'shape')
    const throughMillInfo = getThroughPathObj(maxMillInfo, part.thick)
    let newArr = [
      ...part.path,
      ...throughCurveHoles.map((it: any) => it.path).filter((it: any) => it),
      ...throughMillInfo.map((it: any) => it.shape),
    ]
    newArr.forEach((shape, idx) => {
      if (idx > 0) {
        let area2 = window.ClipperLib.JS.AreaOfPolygon(shape)
        let direction = area2 < 0
        if (direction === firstDirection) {
          shape.reverse()
        }
      }
      shape.forEach((point: { x: number; y: number }, idx: number) => {
        if (!idx) {
          ctx.value?.moveTo(
            startX + calcScale(point.x),
            startY + calcScale(point.y)
          )
        } else {
          ctx.value?.lineTo(
            startX + calcScale(point.x),
            startY + calcScale(point.y)
          )
        }
      })
      if (!idx) {
        clipPath.value = shape.map((point: { x: number; y: number }) => ({
          x: startX + calcScale(point.x),
          y: startY + calcScale(point.y),
        }))
      }
    })
    ctx.value?.stroke()
    ctx.value?.fill()
  }
}
// 缩放
function calcScale(num: number) {
  return num * scale.value
}
function handleZoom(num: number) {
  num > 0 ? scene.value!.zoomIn() : scene.value!.zoomOut()
  zoomPercent.value = +zoomPercent.value + num
  if (zoomPercent.value <= 50) {
    zoomPercent.value = 50
  }
}

// 监听滚轮滚动，修改缩放比例
function handleWheelScale(e: WheelEvent) {
  if (e.deltaY > 0) {
    scene.value!.zoomOut()
  } else {
    scene.value!.zoomIn()
  }
  zoomPercent.value = +(scene.value!.getScaleRate() * 100).toFixed(0)
}

function handleFrameSelect() {
  isClickFrameSelect.value = !isClickFrameSelect.value
}
function handleCloseDepthErrorDialog() {
  emits('onClose')
}
//点击切换异常小板
function handleBtn(type: 'next' | 'prev') {
  emits('onDepthErrorBtn', type)
  emits(
    'onSearch',
    props.depthErrorData.partUniqueId,
    props.depthErrorIndex,
    'depthError'
  )
}
// 绘制选中框
function handleDrawConflictRect() {
  const depthErrorHoleSlot = {
    holes: [
      ...(props.depthErrorData!.holes.filter(
        (item: HoleType) => item.depthError
      ) ?? []),
    ],
    slots: [
      ...(props.depthErrorData!.slots.filter(
        (item: SlotType) => item.depthError
      ) ?? []),
    ],
  }

  let list: {
    left: number[]
    top: number[]
    bottom: number[]
    right: number[]
  }[] = []
  depthErrorHoleSlot['holes'].forEach((hole) => {
    const {
      center: { x, y },
      diameter,
    } = hole
    const info = {
      left: [startXBase.value + (x - diameter / 2) * scale.value - gap],
      right: [startXBase.value + (x + diameter / 2) * scale.value + gap],
      bottom: [startYBase.value + (y + diameter / 2) * scale.value + gap],
      top: [startYBase.value + (y - diameter / 2) * scale.value - gap],
    }
    list.push(info)
  })
  depthErrorHoleSlot['slots'].forEach((slot) => {
    const { path } = getSlotBasicInfo(slot, 'pt')
    const resArr = calculateMinMaxXY(path)
    const info = {
      left: [startXBase.value + resArr!.minX * scale.value - gap],
      right: [startXBase.value + resArr!.maxX * scale.value + gap],
      bottom: [startYBase.value + resArr!.maxY * scale.value + gap],
      top: [startYBase.value + resArr!.minY * scale.value - gap],
    }
    list.push(info)
  })
  scene.value?.drawDashRect(list)
}
function calculateMinMaxXY(points: { x: number; y: number }[]) {
  if (!Array.isArray(points) || points.length === 0) return null // 输入不是有效的点数组
  return points.reduce(
    (acc, point) => {
      acc.minX = Math.min(acc.minX, point.x)
      acc.minY = Math.min(acc.minY, point.y)
      acc.maxX = Math.max(acc.maxX, point.x)
      acc.maxY = Math.max(acc.maxY, point.y)
      return acc
    },
    {
      minX: Infinity,
      minY: Infinity,
      maxX: -Infinity,
      maxY: -Infinity,
    }
  )
}
watch(
  () => props.depthErrorData,
  () => {
    scene.value?.reset()
    scene.value?.draw(startRender)
    isClickFrameSelect.value && handleDrawConflictRect()
  }
)
watch(
  () => isClickFrameSelect.value,
  (newVal) => {
    scene.value!.reset()
    newVal && handleDrawConflictRect()
    scene.value?.changeStatus(newVal)
    if (!newVal) {
      zoomPercent.value = 100
    }
  }
)
watch(
  () => props.isShow,
  (newVal) => {
    if (newVal) {
      nextTick(() => {
        scene.value = new Scene('depthErrorCanvas')
        ctx.value = canvasRef.value!.getContext('2d')
        scene.value?.reset()
        scene.value?.draw(startRender)
      })
      emits(
        'onSearch',
        props.depthErrorData.partUniqueId,
        props.depthErrorIndex,
        'depthError'
      )
    }
  }
)

return { __sfc: true,_startX, _startY, gap, emits, props, scene, canvasRef, ctx, zoomPercent, scale, clipPath, startXBase, startYBase, canvasState, isClickFrameSelect, startRender, drawHoles, drawSlots, drawShape, calcScale, handleZoom, handleWheelScale, handleFrameSelect, handleCloseDepthErrorDialog, handleBtn, handleDrawConflictRect, calculateMinMaxXY, defaultTipsArr }
}

})